import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Shield, Star, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useOnboardingStore } from '@/hooks/use-onboarding-store';

interface Step9ConversionProps {
  onNext: () => void;
}

const Step9Conversion: React.FC<Step9ConversionProps> = ({
  onNext
}) => {
  const {
    selectedPlan,
    setSelectedPlan
  } = useOnboardingStore();

  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState(10 * 60); // 10 minutes in seconds
  const [selectedCountry, setSelectedCountry] = useState('nigeria');
  const planSectionRef = useRef<HTMLDivElement>(null);

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format time for display
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Scroll to plan section
  const scrollToPlanSection = () => {
    planSectionRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };
  const plans = [{
    id: '1-week',
    title: '1-Week No-Code Sprint',
    price: selectedCountry === 'nigeria' ? '₦1,890/day' : '$1.50/day',
    total: selectedCountry === 'nigeria' ? '₦13,300' : '$10.50',
    benefit: 'Perfect for rapid prototyping'
  }, {
    id: '2-week',
    title: '2-Week App Builder Accelerator',
    price: selectedCountry === 'nigeria' ? '₦1,500/day' : '$1.30/day',
    total: selectedCountry === 'nigeria' ? '₦21,000' : '$18.20',
    benefit: 'Build your first functional app',
    recommended: true
  }, {
    id: '4-week',
    title: '4-Week Full Stack No-Code Mastery',
    price: selectedCountry === 'nigeria' ? '₦1,300/day' : '$1.20/day',
    total: selectedCountry === 'nigeria' ? '₦37,000' : '$37.20',
    benefit: 'Become a no-code development expert'
  }];
  const testimonials = [{
    text: "As a non-coder, I never thought I could build my own internal tool. This course made it incredibly easy and fast, allowing me to automate processes for my business. Highly recommended!",
    author: "Jane D., Small Business Owner",
    rating: 5
  }, {
    text: "I launched my first SaaS idea in less than a month using the no-code methods here. It's a game-changer for entrepreneurs looking to validate and build quickly without massive development costs!",
    author: "Mark S., Startup Founder",
    rating: 5
  }, {
    text: "The AI integration tips alone were worth it! I'm now building stunning websites faster and smarter than ever before, dramatically boosting my freelance services.",
    author: "Emily R., Freelance Designer",
    rating: 5
  }];
  const selectedPlanData = plans.find(p => p.id === selectedPlan);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="w-full max-w-md mx-auto bg-white min-h-screen overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex justify-between items-center">
        <div className={`text-sm font-medium flex items-center gap-2 ${timeLeft < 300 ? 'text-red-600' : 'text-gray-600'}`}>
          <span className={`${timeLeft < 300 ? 'animate-pulse' : ''}`}>⏰</span>
          <span className="font-mono">{formatTime(timeLeft)}</span>
          {timeLeft < 300 && <span className="text-xs text-red-500">Limited time!</span>}
        </div>
        <Button onClick={scrollToPlanSection} className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
          GET MY PLAN
        </Button>
      </div>

      <div className="px-4 pb-6 space-y-6">
        {/* Readiness Score Section - Redesigned */}
        <div className="relative px-6 py-8 mx-4 bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 rounded-3xl shadow-xl border border-white/20 backdrop-blur-sm">
          {/* Floating Background Elements */}
          <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-xl"></div>
          <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-full blur-xl"></div>
          
          <div className="relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg mb-4 transform rotate-3">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <div className="w-4 h-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-sm"></div>
                </div>
              </div>
              
              <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
                Your No-Code Building Potential
              </h2>
              
              <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mx-auto"></div>
            </div>
            
            <div className="flex justify-center gap-6 mb-8">
              <div className="flex-1 max-w-32">
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/30">
                  <div className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Idea to Launch</div>
                  
                  <div className="relative">
                    <div className="w-full h-3 bg-gray-200/50 rounded-full overflow-hidden shadow-inner">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full shadow-sm transition-all duration-1000 ease-out" style={{
                        width: '85%'
                      }}></div>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full shadow-lg border-2 border-white" style={{
                      left: '85%',
                      transform: 'translateX(-50%)'
                    }}></div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 mt-3 font-medium">
                    <span>Challenge</span>
                    <span>Reality</span>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 max-w-32">
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/30">
                  <div className="text-xs font-medium text-gray-600 mb-3 uppercase tracking-wide">Digital Ownership</div>
                  
                  <div className="relative">
                    <div className="w-full h-3 bg-gray-200/50 rounded-full overflow-hidden shadow-inner">
                      <div className="h-full bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-sm transition-all duration-1000 ease-out" style={{
                        width: '90%'
                      }}></div>
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full shadow-lg border-2 border-white" style={{
                      left: '90%',
                      transform: 'translateX(-50%)'
                    }}></div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 mt-3 font-medium">
                    <span>Limited</span>
                    <span>Complete</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-2xl shadow-lg mb-4 transform -rotate-1">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  <span className="text-lg font-bold">Your readiness score: 87%</span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 leading-relaxed max-w-xs mx-auto">
                This score reflects your potential to build and own robust digital products without code, leveraging AI.
              </p>
            </div>
          </div>
        </div>

        {/* Program Introduction */}
        <div className="text-center rounded-xl overflow-hidden">
          <img src="/lovable-uploads/42ad591f-99b8-4b29-b689-6c1aee73396b.png" alt="Your Fast-Track Program to Launch Anything Online with No-Code & AI" className="w-full h-auto rounded-xl" />
        </div>

        {/* Core Value Proposition */}
        <div className="text-center">
          <h3 className="font-semibold text-gray-800 mb-3">Your 28-Day No-Code Launchpad is Ready!</h3>
          
          <div className="space-y-2 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <span>🏗️</span>
              <span>Build functional web apps & internal tools</span>
            </div>
            <div className="flex items-center gap-2">
              <span>✨</span>
              <span>Design stunning, high-converting websites & landing pages</span>
            </div>
            <div className="flex items-center gap-2">
              <span>🚀</span>
              <span>Launch your SaaS idea without writing a line of code</span>
            </div>
            <div className="flex items-center gap-2">
              <span>🤖</span>
              <span>Master AI tools to accelerate your development process</span>
            </div>
            <div className="flex items-center gap-2">
              <span>💡</span>
              <span>Own your digital creations from idea to deployment</span>
            </div>
          </div>
        </div>

        {/* Social Proof */}
        <div className="text-center bg-green-50 rounded-xl p-3">
          <p className="text-sm font-medium text-green-700">
            Over 1,350+ creators launched their ideas with our No-Code & AI program today!
          </p>
        </div>

        {/* Plan Selection */}
        <div ref={planSectionRef}>
          <h3 className="font-semibold text-gray-800 mb-4 text-center">Choose your plan</h3>
          
          {/* Country Selection */}
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedCountry('nigeria')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  selectedCountry === 'nigeria'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">🇳🇬</div>
                  <div className="font-semibold text-gray-900 text-xs">Nigeria/Africa</div>
                  <div className="text-xs text-blue-600 font-medium">Naira pricing</div>
                </div>
              </button>
              <button
                onClick={() => setSelectedCountry('international')}
                className={`p-3 rounded-xl border-2 transition-all ${
                  selectedCountry === 'international'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl mb-1">🌍</div>
                  <div className="font-semibold text-gray-900 text-xs">International</div>
                  <div className="text-xs text-blue-600 font-medium">USD pricing</div>
                </div>
              </button>
            </div>
          </div>
          
          <div className="space-y-3">
            {plans.map(plan => <div key={plan.id} onClick={() => setSelectedPlan(plan.id)} className={`border-2 rounded-xl p-3 cursor-pointer transition-all ${selectedPlan === plan.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'} ${plan.recommended ? 'ring-2 ring-blue-200' : ''}`}>
                {plan.recommended && <div className="text-xs font-medium text-blue-600 mb-1">RECOMMENDED</div>}
                <div className="font-semibold text-sm text-gray-800">{plan.title}</div>
                <div className="text-xs text-gray-500">{plan.benefit}</div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm font-medium text-gray-700">{plan.price}</span>
                  <span className="text-lg font-bold text-gray-800">{plan.total}</span>
                </div>
              </div>)}
          </div>
        </div>

        {/* CTA Button */}
        <Button onClick={onNext} className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-xl text-lg">
          GET MY PLAN
        </Button>

        {/* Payment Disclosure */}
        <div className="text-xs text-gray-500 text-center leading-relaxed">
          Unlock by clicking GET MY PLAN and you agree to pay {selectedPlanData?.total} for {selectedPlanData?.title}. 
          You will be redirected to the secure checkout page where you can complete your purchase. 
          By proceeding, you agree to iSelect Digital's{' '}
          <span className="text-blue-500 underline">Terms & Conditions</span> and{' '}
          <span className="text-blue-500 underline">Privacy Policy</span>.
        </div>

        {/* Payment Methods */}
        <div className="text-center">
          <div className="flex justify-center gap-2 text-xs text-gray-400">
            <span>VISA</span>
            <span>•</span>
            <span>Mastercard</span>
            <span>•</span>
            <span>Verve</span>
            <span>•</span>
            <span>Google Pay</span>
          </div>
        </div>

        {/* Money-Back Guarantee */}
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-800">Money-Back Guarantee</span>
          </div>
          <p className="text-xs text-green-700 leading-relaxed">
            We are confident in our course that we are ready to offer you a 100% money-back guarantee 
            within 30 days of purchase if you are not satisfied. Learn how to build and launch with confidence!
          </p>
        </div>

        {/* App Access Section */}
        <div className="text-center">
          <h4 className="font-semibold text-gray-800 mb-3">Build, Launch, Own with Lovabuild, Anywhere.</h4>
          
          <div className="flex justify-center mb-4">
            <img src="/lovable-uploads/d7f740f0-633f-4615-80a2-546af082e071.png" alt="Web App Builder, Landing Page, and Dashboard interfaces" className="w-full max-w-sm h-auto" />
          </div>
        </div>

        {/* What You Get */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-3">What you get</h4>
          
          <div className="space-y-2 text-xs text-gray-600">
            <div className="flex items-start gap-2">
              <span>🚀</span>
              <span>Step-by-step guides to building functional web apps & SaaS without code.</span>
            </div>
            <div className="flex items-start gap-2">
              <span>🎨</span>
              <span>Master visual development platforms like Bubble, Webflow, Adalo, and more.</span>
            </div>
            <div className="flex items-start gap-2">
              <span>⚡️</span>
              <span>Leverage AI for rapid prototyping, content generation, and smart feature integration.</span>
            </div>
            <div className="flex items-start gap-2">
              <span>💡</span>
              <span>Techniques for validating, launching, and marketing your no-code product.</span>
            </div>
            <div className="flex items-start gap-2">
              <span>📈</span>
              <span>Strategies to own and scale your digital creations, ensuring long-term success.</span>
            </div>
            <div className="flex items-start gap-2">
              <span>🤝</span>
              <span>Access to our exclusive community of no-code builders.</span>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-4 text-center">People are Building Amazing Things with Lovabuild!</h4>
          
          <div className="space-y-4">
            {testimonials.map((testimonial, index) => <div key={index} className="bg-gray-50 rounded-xl p-3">
                <div className="flex gap-1 mb-2">
                  {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-xs text-gray-700 mb-2 leading-relaxed">"{testimonial.text}"</p>
                <p className="text-xs text-gray-500 font-medium">{testimonial.author}</p>
              </div>)}
          </div>
        </div>
      </div>
    </motion.div>;
};
export default Step9Conversion;